import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, MenuItem, Menu, Avatar } from "@mui/material";
import { USER } from "../constants/userCred";
import { useAuth } from "../context/AuthContext";
// @ts-ignore - PDF import handled by custom.d.ts
import userGuidPdf from "../assets/docs/User-Guide.pdf";
//import { IMeetingReducerState } from "../interfaces/store/IMeetingReducerState";
import { AppDispatch } from "../store/store";
import { useDispatch, useSelector } from "react-redux";
import { actionTypes } from "../store/actionTypes/actionTypes";
import { useMsal } from "@azure/msal-react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { useNavigate } from "react-router-dom";
import { RouterPathEnum } from "../constants/router";
import { IUserDetailsPermissionState } from "../interfaces/store/IUserDetailsPermissionState";
import { COMMON } from "../constants/common";

interface AppHeaderProps {
  showLogout: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({ showLogout }) => {
  const navigate = useNavigate();
  const { updateUserRole, userRole } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { instance } = useMsal();

  // Get user details from Redux store
  const userDetails = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.userDetails
  ) || (() => {
    // Fallback to localStorage if Redux doesn't have it
    const stored = localStorage.getItem(COMMON.USER_DETAILS_LS_KEY.DETAILS);
    return stored ? JSON.parse(stored) : null;
  })();

 /*  const zoomMeetingId = useSelector(
    (state: { meeting: IMeetingReducerState }) => state.meeting.meetingId
  ); */

  const [anchorElAdmin, setAnchorElAdmin] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenAdminMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAdmin(event.currentTarget);
  };
  const handleCloseAdminMenu = () => {
    setAnchorElAdmin(null);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem(USER.LS_USER_ROLE_KEY);
      localStorage.clear();
      updateUserRole(null);
      dispatch({
        type: actionTypes.UPDATE_MEETING_ID,
        payload: null,
      });
      dispatch({
        type: actionTypes.UPDATE_FETCH_TRANS_ID,
        payload: null,
      });

      //NOTE:: IF Need logout popup then we can use this
      // instance.logoutPopup({
      //   postLogoutRedirectUri: "/login",
      //   mainWindowRedirectUri: "/login",
      // });

      //NOTE:: Here we used logout using redirect
      await instance.logoutRedirect({
        postLogoutRedirectUri: "/login",
      });
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleDownloadUserGuidPdf = async () => {
    window.open(userGuidPdf, "_blank");
  };
  const handleHeaderTitleClick = async () => {
    navigate(`/${RouterPathEnum.DGO}/${RouterPathEnum.DASHBOARD}`);
    dispatch({
      type: actionTypes.UPDATE_MEETING_ID,
      payload: null,
    });
    dispatch({
      type: actionTypes.UPDATE_FETCH_TRANS_ID,
      payload: null,
    });
  };

  // Generate user initials for avatar
  const getInitials = (): string => {
    if (!userDetails) return "U";
    const name = userDetails.name || userDetails.firstName || userDetails.email || "";
    if (!name) return "U";
    
    // Split name into parts
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      // First letter of first name + first letter of last name
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    } else if (nameParts.length === 1) {
      // Single name - take first two letters
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return "U";
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string): string => {
    const colors = [
      "#e91e63", // pink
      "#2196f3", // blue
      "#4caf50", // green
      "#ff9800", // orange
      "#9c27b0", // purple
      "#f44336", // red
      "#00bcd4", // cyan
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  // Format role name for display
  const formatRoleName = (role: string | null | undefined): string => {
    if (!role) return "";
    
    const roleUpper = role.toUpperCase();
    switch (roleUpper) {
      case "ADMIN":
        return "Admin";
      case "VIEWER":
        return "Viewer";
      case "RAPPORTEUR":
        return "Rapporteur";
      case "SUPER_ADMIN":
        return "Super Admin";
      default:
        // If role doesn't match, return formatted version (e.g., "SUPER_ADMIN" -> "Super Admin")
        return role
          .split("_")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
    }
  };

  return (
    <AppBar className='header-container'>
      <Toolbar
        sx={{ paddingLeft: "0px !important", paddingRight: "0px !important" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              variant='h6'
              sx={{
                color: "#ffffff",
                cursor: "pointer",
              }}
              title='Click here to go to home page'
              onClick={handleHeaderTitleClick}
            >
              Real-Time Reporting Tool
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {(userRole === USER.USER_ROLE.ADMIN || userRole === USER.USER_ROLE.SUPER_ADMIN) && (
              <>
                <Box onClick={handleOpenAdminMenu} sx={{ display: "flex" }}>
                  <Box className='admin-text'>Setup</Box>
                  {anchorElAdmin ? (
                    <KeyboardArrowUpRoundedIcon className='menu-icon-color' />
                  ) : (
                    <KeyboardArrowDownRoundedIcon className='menu-icon-color' />
                  )}
                </Box>
              </>
            )} 
 
            <IconButton color='inherit' onClick={handleDownloadUserGuidPdf}>
              <HelpOutlineIcon />
            </IconButton>
            
            {/* User Avatar Circle with Menu */}
            {userDetails && (
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{
                  ml: 2,
                  mr: 1,
                  p: 0,
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: getAvatarColor(
                      userDetails.name || userDetails.firstName || userDetails.email || "User"
                    ),
                    width: 40,
                    height: 40,
                    cursor: "pointer",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {getInitials()}
                </Avatar>
              </IconButton>
            )}
          </Box>
          <Menu
            sx={{ mt: "35px" }}
            id='menu-appbar'
            anchorEl={anchorElAdmin}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElAdmin)}
            onClose={handleCloseAdminMenu}
          >
            <MenuItem
              onClick={() => {
                navigate(
                  `/${RouterPathEnum.DGO}/${RouterPathEnum.ADMIN}/${RouterPathEnum.MEETINGS}`
                );
                handleCloseAdminMenu();
              }}
            >
              <Typography textAlign='center'>Meetings</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate(
                  `/${RouterPathEnum.DGO}/${RouterPathEnum.ADMIN}/${RouterPathEnum.INTERVENTION_LABEL}`
                );
                handleCloseAdminMenu();
              }}
            >
              <Typography textAlign='center'>
                Intervention Label Management
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate(
                  `/${RouterPathEnum.DGO}/${RouterPathEnum.ADMIN}/${RouterPathEnum.ACRONYMS}`
                );
                handleCloseAdminMenu();
              }}
            >
              <Typography textAlign='center'>Acronym Management</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate(
                  `/${RouterPathEnum.DGO}/${RouterPathEnum.ADMIN}/${RouterPathEnum.USER_MANAGEMENT}`
                );
                handleCloseAdminMenu();
              }}
            >
              <Typography textAlign='center'>
                User Management
              </Typography>
            </MenuItem>
          </Menu>
          
          {/* User Menu with Username (Role) and Logout */}
          <Menu
            sx={{ mt: "45px" }}
            id='menu-user'
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            PaperProps={{
              sx: {
                minWidth: 200,
                mt: 1,
              },
            }}
          >
            {/* Username (Role) Display - Not clickable */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {userDetails?.email || "User"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "12px",
                }}
              >
                {formatRoleName(userDetails?.roleName || userRole)}
              </Typography>
            </Box>
            
            {showLogout && (
              <MenuItem
                onClick={() => {
                  handleCloseUserMenu();
                  handleLogout();
                }}
                sx={{
                  color: "red",
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: "rgba(255, 0, 0, 0.1)",
                  },
                }}
              >
                <Typography textAlign='center' fontWeight={500} sx={{ width: "100%" }}>
                  Logout
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
